import { type VariantProps, cva } from "class-variance-authority";
import { type JSX, type ValidComponent, splitProps } from "solid-js";
import { cn } from "~/util/utils";
import { Button, type ButtonProps } from "./button";

const iconVariants = cva("inline-flex", {
	variants: {
		size: {
			default: "size-4 min-h-4 min-w-4 max-h-4 max-w-4",
			xs: "size-4 min-h-4 min-w-4 max-h-4 max-w-4",
			sm: "size-5 min-h-5 min-w-5 max-h-5 max-w-5",
			md: "size-6 min-h-6 min-w-6 max-h-6 max-w-6",
			lg: "size-7 min-h-7 min-w-7 max-h-7 max-w-7",
			xl: "size-8 min-h-8 min-w-8 max-h-8 max-w-8",
			"2xl": "size-9 min-h-9 min-w-9 max-h-9 max-w-9",
			"3xl": "size-10 min-h-10 min-w-10 max-h-10 max-w-10",
		},
		disabled: {
			true: "opacity-50 pointer-events cursor-not-allowed",
		},
		left: {
			true: "mr-2",
		},
		right: {
			true: "ml-2",
		},
	},
	defaultVariants: {
		size: "default",
		disabled: false,
	},
});

interface IconProps extends JSX.SvgSVGAttributes<SVGSVGElement>, VariantProps<typeof iconVariants> {
	path: string;
	children?: never;
}

function Icon(props: IconProps) {
	const [variants, local, others] = splitProps(
		props as IconProps,
		["disabled", "left", "right", "size"],
		["class", "path"],
	);

	return (
		<svg
			class={cn(iconVariants(variants), local.class)}
			viewBox="0 0 24 24"
			{...others}
		>
			<path
				class="fill-inherit"
				d={local.path}
			/>
		</svg>
	);
}

interface ActionIconProps<T extends ValidComponent = "button"> extends Omit<ButtonProps<T>, "children"> {
	path: string;
	iconProps?: Omit<IconProps, "path">;
	spin?: boolean;
}

function ActionIcon<T extends ValidComponent = "button">(
	props: ActionIconProps<T> & JSX.HTMLAttributes<HTMLButtonElement>,
) {
	const [local, variants, other] = splitProps(props, ["class", "iconProps", "path", "spin"], ["disabled", "size"]);

	return (
		<Button<"button">
			class={cn(
				iconVariants({ ...variants, size: variants.size || "3xl" }),
				"p-1 rounded-md items-center justify-center",
				local.class,
			)}
			variant="outline"
			disabled={variants.disabled}
			{...other}
		>
			<Icon
				path={local.path}
				{...local.iconProps}
				class={cn(local.spin && "animate-spin", local.iconProps?.class)}
			/>
		</Button>
	);
}

export type { IconProps, ActionIconProps };
export { Icon, iconVariants, ActionIcon };
